import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ColorPicker from '../components/ColorPicker';
import Slider from '../components/Slider';
import Toggle from '../components/Toggle';
import TextInput from '../components/TextInput';
import { mockupService } from '../services/mockupService';
import StepProgress from '../components/StepProgress';
import { ArrowLeft } from 'lucide-react';
import PromptHelper from '../components/PromptHelper';
import MockupFlowSummary from '../components/MockupFlowSummary';
import {
  clearCustomizationDraft,
  formatSavedTimestamp,
  formatTemplateName,
  getPrimarySourceLabel,
  readCustomizationDraft,
  readSelectedTemplateDetails,
  resolveTemplateThumbnailUrl,
  type SelectedTemplateDetails,
  writeSelectedTemplateDetails,
  writeCustomizationDraft,
} from '../utils/mockupFlow';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_PRODUCT_COLOR = '#3b82f6';

const Customize: React.FC = () => {
  const navigate = useNavigate();
  
  // Customization states
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_BACKGROUND_COLOR);
  const [useBackgroundColor, setUseBackgroundColor] = useState(true);
  const [backgroundDescription, setBackgroundDescription] = useState('');
  const [productColor, setProductColor] = useState(DEFAULT_PRODUCT_COLOR);
  const [useProductColor, setUseProductColor] = useState(true);
  const [showShadow, setShowShadow] = useState(true);
  const [showReflection, setShowReflection] = useState(false);
  const [glossyFinish, setGlossyFinish] = useState(false);
  const [embossEffect, setEmbossEffect] = useState(false);
  const [gradientOverlay, setGradientOverlay] = useState(false);
  const [productTitle, setProductTitle] = useState('');
  const [productSubtitle, setProductSubtitle] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [scale, setScale] = useState(100);
  const [aspectRatio, setAspectRatio] = useState('square');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState('Generating your mockup...');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [primaryImageId, setPrimaryImageId] = useState('');
  const [primaryImagePreviewUrl, setPrimaryImagePreviewUrl] = useState('');
  const [secondaryImageId, setSecondaryImageId] = useState<string | null>(null);
  const [brandColorDefaults, setBrandColorDefaults] = useState({
    background: DEFAULT_BACKGROUND_COLOR,
    product: DEFAULT_PRODUCT_COLOR,
  });
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [draftRestoredAt, setDraftRestoredAt] = useState<string | null>(null);
  const [draftReady, setDraftReady] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState<SelectedTemplateDetails | null>(() => readSelectedTemplateDetails());
  const selectedTemplateThumbnailUrl = resolveTemplateThumbnailUrl(selectedTemplateDetails?.thumbnailUrl);
  const selectedTemplateLabel = selectedTemplateDetails?.name || formatTemplateName(selectedTemplate);

  // Load template and file info from session storage
  useEffect(() => {
    const template = sessionStorage.getItem('selectedTemplate');
    const pId = sessionStorage.getItem('primaryImageId');
    const primaryImageUrl = sessionStorage.getItem('primaryImageUrl');
    const sId = sessionStorage.getItem('secondaryImageId');
    const brandColorsStr = sessionStorage.getItem('brandColors');
    const brandSettingsStr = sessionStorage.getItem('brandSettings');

    if (!template || !pId) {
      navigate('/dashboard/create');
      return;
    }

    const normalizedSecondaryImageId = sId && sId.trim() !== '' ? sId : null;
    setSelectedTemplate(template);
    setPrimaryImageId(pId);
    setPrimaryImagePreviewUrl(primaryImageUrl || '');
    setSecondaryImageId(normalizedSecondaryImageId);

    let nextBackgroundColor = DEFAULT_BACKGROUND_COLOR;
    let nextProductColor = DEFAULT_PRODUCT_COLOR;

    if (brandColorsStr) {
      try {
        const brandColors = JSON.parse(brandColorsStr);
        if (brandColors.primary) {
          nextBackgroundColor = brandColors.primary;
        }
        if (brandColors.secondary) {
          nextProductColor = brandColors.secondary;
        }
        console.log('Loaded brand colors:', brandColors);
      } catch (err) {
        console.error('Failed to parse brand colors:', err);
      }
    }

    // Parse expanded brand settings (includes identity fields)
    if (brandSettingsStr) {
      try {
        const parsed = JSON.parse(brandSettingsStr);
        // Store in sessionStorage keys that handleGenerate will read
        if (parsed.brandStyle) sessionStorage.setItem('brandStyle', parsed.brandStyle);
        if (parsed.brandDescription) sessionStorage.setItem('brandDescription', parsed.brandDescription);
        if (parsed.industry) sessionStorage.setItem('brandIndustry', parsed.industry);
        if (parsed.typographyStyle) sessionStorage.setItem('brandTypographyStyle', parsed.typographyStyle);
        if (parsed.accent) sessionStorage.setItem('brandAccentColor', parsed.accent);
        console.log('Loaded brand settings:', parsed);
      } catch (err) {
        console.error('Failed to parse brand settings:', err);
      }
    }

    setBrandColorDefaults({
      background: nextBackgroundColor,
      product: nextProductColor,
    });
    setBackgroundColor(nextBackgroundColor);
    setProductColor(nextProductColor);

    const draft = readCustomizationDraft();
    if (
      draft &&
      draft.template === template &&
      draft.primaryImageId === pId &&
      (draft.secondaryImageId || null) === normalizedSecondaryImageId
    ) {
      const { values } = draft;
      setBackgroundColor(values.backgroundColor);
      setUseBackgroundColor(values.useBackgroundColor);
      setBackgroundDescription(values.backgroundDescription);
      setProductColor(values.productColor);
      setUseProductColor(values.useProductColor);
      setShowShadow(values.showShadow);
      setShowReflection(values.showReflection);
      setGlossyFinish(values.glossyFinish);
      setEmbossEffect(values.embossEffect);
      setGradientOverlay(values.gradientOverlay);
      setProductTitle(values.productTitle);
      setProductSubtitle(values.productSubtitle);
      setCustomPrompt(values.customPrompt);
      setScale(values.scale);
      setAspectRatio(values.aspectRatio);
      setDraftRestoredAt(draft.updatedAt);
      setDraftSavedAt(draft.updatedAt);
    } else {
      setDraftRestoredAt(null);
      setDraftSavedAt(null);
    }

    setDraftReady(true);
    sessionStorage.setItem('creationStep', '3');
  }, [navigate]);

  useEffect(() => {
    if (!selectedTemplate) {
      setSelectedTemplateDetails(null);
      return;
    }

    const storedDetails = readSelectedTemplateDetails();
    if (storedDetails?.id === selectedTemplate) {
      setSelectedTemplateDetails(storedDetails);
      if (storedDetails.thumbnailUrl) {
        return;
      }
    }

    let isActive = true;

    const hydrateTemplateDetails = async () => {
      try {
        const response = await mockupService.getTemplates();
        if (!response.success) {
          return;
        }

        const matchedTemplate = response.templates.find((template) => template.id === selectedTemplate);
        if (!matchedTemplate || !isActive) {
          return;
        }

        setSelectedTemplateDetails(matchedTemplate);
        writeSelectedTemplateDetails(matchedTemplate);
      } catch (error) {
        console.error('Failed to load selected template details:', error);
      }
    };

    void hydrateTemplateDetails();

    return () => {
      isActive = false;
    };
  }, [selectedTemplate]);

  useEffect(() => {
    if (!draftReady || !selectedTemplate || !primaryImageId) {
      return;
    }

    const nextDraft = {
      template: selectedTemplate,
      primaryImageId,
      secondaryImageId,
      values: {
        backgroundColor,
        useBackgroundColor,
        backgroundDescription,
        productColor,
        useProductColor,
        showShadow,
        showReflection,
        glossyFinish,
        embossEffect,
        gradientOverlay,
        productTitle,
        productSubtitle,
        customPrompt,
        scale,
        aspectRatio,
      },
      updatedAt: new Date().toISOString(),
    };

    writeCustomizationDraft(nextDraft);
    setDraftSavedAt(nextDraft.updatedAt);
  }, [
    aspectRatio,
    backgroundColor,
    backgroundDescription,
    customPrompt,
    draftReady,
    embossEffect,
    glossyFinish,
    gradientOverlay,
    primaryImageId,
    productColor,
    productSubtitle,
    productTitle,
    scale,
    secondaryImageId,
    selectedTemplate,
    showReflection,
    showShadow,
    useBackgroundColor,
    useProductColor,
  ]);

  // Prevent accidental navigation during generation
  useEffect(() => {
    if (!isGenerating) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isGenerating]);

  // Rotating progress messages during generation
  useEffect(() => {
    if (!isGenerating) {
      setProgressMessage('Generating your mockup...');
      return;
    }
    const messages = [
      'Generating your mockup...',
      'AI is creating your design...',
      'Composing the perfect layout...',
      'Almost there...'
    ];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setProgressMessage(messages[index]);
    }, 8000);
    return () => clearInterval(interval);
  }, [isGenerating]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  const handleGenerate = async () => {
    if (!selectedTemplate || !primaryImageId) {
      setError('Missing template or primary image. Please go back and complete the previous steps.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 90000);

    try {
      // Handle brand logo and asset library cases
      const useBrandLogo = sessionStorage.getItem('useBrandLogo') === 'true';
      const primaryImageUrl = sessionStorage.getItem('primaryImageUrl');
      const secondaryImageUrl = sessionStorage.getItem('secondaryImageUrl');
      
      // Prepare custom prompt with background description if applicable
      let finalCustomPrompt = customPrompt;
      if (!useBackgroundColor && backgroundDescription) {
        finalCustomPrompt = backgroundDescription + (customPrompt ? `, ${customPrompt}` : '');
      }

      // Read brand identity fields from sessionStorage
      const brandStyle = sessionStorage.getItem('brandStyle') || undefined;
      const brandDescription = sessionStorage.getItem('brandDescription') || undefined;
      const brandIndustry = sessionStorage.getItem('brandIndustry') || undefined;
      const brandTypographyStyle = sessionStorage.getItem('brandTypographyStyle') || undefined;
      const brandAccentColor = sessionStorage.getItem('brandAccentColor') || undefined;

      const generateOptions: any = {
        primaryImageId: primaryImageId,
        template: selectedTemplate,
        backgroundColor: useBackgroundColor ? backgroundColor : 'transparent',
        productColor: useProductColor ? productColor : undefined,
        accentColor: brandAccentColor,
        productTitle,
        productSubtitle,
        customPrompt: finalCustomPrompt,
        showShadow,
        showReflection,
        glossyFinish,
        embossEffect,
        gradientOverlay,
        scale,
        aspectRatio,
        brandStyle,
        brandDescription: brandDescription,
        industry: brandIndustry,
        typographyStyle: brandTypographyStyle,
      };
      
      // Only add secondaryImageId if it exists
      if (secondaryImageId && secondaryImageId.trim() !== '') {
        generateOptions.secondaryImageId = secondaryImageId;
      }
      
      // Add primaryImageUrl if using brand logo or asset library
      if ((useBrandLogo || primaryImageUrl) && primaryImageUrl) {
        generateOptions.primaryImageUrl = primaryImageUrl;
      }
      
      // Add secondaryImageUrl if from asset library
      if (secondaryImageUrl) {
        generateOptions.secondaryImageUrl = secondaryImageUrl;
      }
      
      const response = await mockupService.generateMockup(generateOptions);

      if (response.success && response.mockups && response.mockups.length > 0) {
        sessionStorage.setItem('lastGenerateOptions', JSON.stringify(generateOptions));
        sessionStorage.setItem('generatedMockupId', response.mockups[0].id);
        sessionStorage.setItem('generatedMockup', JSON.stringify(response.mockups[0]));
        sessionStorage.setItem('creationStep', '4');
        navigate('/dashboard/download');
      } else {
        throw new Error(response.error || 'Failed to generate mockup');
      }
    } catch (err: any) {
      console.error('Error generating mockup:', err);
      if (err.name === 'AbortError' || controller.signal.aborted) {
        setError('Generation is taking longer than expected. Please try again.');
      } else {
        setError(err.message || 'Failed to generate mockup. Please try again.');
      }
    } finally {
      clearTimeout(timeoutId);
      abortControllerRef.current = null;
      setIsGenerating(false);
    }
  };

  const steps = [
    { number: 1, title: 'Choose Template', description: 'Select mockup type' },
    { number: 2, title: 'Upload Assets', description: 'Add your images' },
    { number: 3, title: 'Customize', description: 'Adjust settings' },
    { number: 4, title: 'Ready', description: 'Review and export' }
  ];

  const enabledEffectsCount = [
    showShadow,
    showReflection,
    glossyFinish,
    embossEffect,
    gradientOverlay,
  ].filter(Boolean).length;

  const handleResetDraft = () => {
    clearCustomizationDraft();
    setBackgroundColor(brandColorDefaults.background);
    setUseBackgroundColor(true);
    setBackgroundDescription('');
    setProductColor(brandColorDefaults.product);
    setUseProductColor(true);
    setShowShadow(true);
    setShowReflection(false);
    setGlossyFinish(false);
    setEmbossEffect(false);
    setGradientOverlay(false);
    setProductTitle('');
    setProductSubtitle('');
    setCustomPrompt('');
    setScale(100);
    setAspectRatio('square');
    setDraftRestoredAt(null);
    setDraftSavedAt(null);
  };

  const flowSummaryItems = [
    { label: 'Template', value: selectedTemplateLabel, tone: 'default' as const },
    { label: 'Primary Source', value: getPrimarySourceLabel(), tone: 'success' as const },
    { label: 'Draft', value: draftSavedAt ? `Autosaved ${formatSavedTimestamp(draftSavedAt)}` : 'No draft yet', tone: draftSavedAt ? 'success' as const : 'warning' as const },
    { label: 'Effects Enabled', value: enabledEffectsCount ? `${enabledEffectsCount} active` : 'No effects selected', tone: enabledEffectsCount ? 'success' as const : 'default' as const },
  ];

  return (
    <div>
      <StepProgress
        currentStep={3}
        steps={steps}
        helperText="Customization is autosaved while you work. Tune the visual direction here before generating the final mockup."
      />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/dashboard/create')}
            disabled={isGenerating}
            className="flex items-center text-body-grey hover:text-text-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Upload
          </button>
          {selectedTemplate && (
            <span className="ml-auto text-sm text-body-grey">
              Template: <span className="font-medium text-text-dark">{selectedTemplateLabel}</span>
            </span>
          )}
        </div>
        
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Customize Your Mockup
          </h1>
          <p className="text-lg text-gray-600">
            Fine-tune your mockup to perfection
          </p>
        </div>

      <div className="mb-8 space-y-4">
        <MockupFlowSummary
          title="Flow Status"
          description="Your selected template and asset source stay visible here while you iterate on the design."
          items={flowSummaryItems}
        />

        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-900">Draft autosave is on</p>
              <p className="mt-1 text-sm text-blue-800">
                {draftRestoredAt
                  ? `Restored your last customization draft from ${formatSavedTimestamp(draftRestoredAt)}.`
                  : 'Changes on this page are saved automatically while you work.'}
              </p>
            </div>
            <button
              onClick={handleResetDraft}
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-blue-300 bg-white px-4 py-2 text-sm font-semibold text-blue-900 transition-colors hover:bg-blue-100"
            >
              Reset Draft
            </button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Preview Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Live template preview</p>
                <h2 className="mt-1 text-xl font-semibold text-gray-900">{selectedTemplateLabel}</h2>
                <p className="mt-1 text-sm text-gray-600">
                  The final AI render will vary, but this shows the selected mockup composition before you generate.
                </p>
              </div>
              <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                {aspectRatio}
              </div>
            </div>
            <div 
              className="aspect-square rounded-lg flex items-center justify-center relative overflow-hidden border border-gray-100"
              style={{ 
                backgroundColor: useBackgroundColor ? backgroundColor : '#f3f4f6',
                backgroundImage: !useBackgroundColor ? 'repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%) 50% / 20px 20px' : 'none'
              }}
            >
              {selectedTemplateThumbnailUrl ? (
                <img
                  src={selectedTemplateThumbnailUrl}
                  alt={selectedTemplateLabel}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-8 text-center text-sm font-medium text-gray-500">
                  Template preview unavailable for this mockup.
                </div>
              )}

              <div
                className="absolute inset-0"
                style={{
                  background: useBackgroundColor ? backgroundColor : 'transparent',
                  opacity: useBackgroundColor ? 0.16 : 0,
                }}
              />

              {gradientOverlay && (
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(160deg, ${useProductColor ? `${productColor}33` : 'rgba(15, 23, 42, 0.16)'} 0%, rgba(15, 23, 42, 0) 60%)`,
                  }}
                />
              )}

              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="rounded-2xl bg-black/55 p-5 backdrop-blur-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/70">Preview context</p>
                  <p className="mt-2 text-lg font-semibold text-white">{selectedTemplateLabel}</p>
                  {productTitle && (
                    <p
                      className="mt-3 text-2xl font-bold"
                      style={{ color: useProductColor ? productColor : '#ffffff' }}
                    >
                      {productTitle}
                    </p>
                  )}
                  {productSubtitle && (
                    <p
                      className="mt-1 text-sm"
                      style={{ color: useProductColor ? `${productColor}DD` : 'rgba(255,255,255,0.85)' }}
                    >
                      {productSubtitle}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2 text-xs font-medium">
                    <span className="rounded-full bg-white/10 px-3 py-1 text-white">Scale {scale}%</span>
                    {showShadow && <span className="rounded-full bg-white/10 px-3 py-1 text-white">Shadow</span>}
                    {showReflection && <span className="rounded-full bg-white/10 px-3 py-1 text-white">Reflection</span>}
                    {glossyFinish && <span className="rounded-full bg-white/10 px-3 py-1 text-white">Glossy</span>}
                    {embossEffect && <span className="rounded-full bg-white/10 px-3 py-1 text-white">Emboss</span>}
                  </div>
                </div>
              </div>

              {primaryImagePreviewUrl && (
                <div className="absolute right-5 top-5 w-28 overflow-hidden rounded-2xl border border-white/70 bg-white/90 shadow-xl backdrop-blur">
                  <div className="aspect-square bg-white">
                    <img
                      src={primaryImagePreviewUrl}
                      alt="Primary asset preview"
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                  <div className="border-t border-gray-100 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">Primary asset</p>
                  </div>
                </div>
              )}

              {showShadow && (
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/10 to-transparent" />
              )}

              {showReflection && (
                <div className="absolute inset-x-8 bottom-4 h-10 rounded-full bg-white/20 blur-2xl" />
              )}
              
              {isGenerating && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-6 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-900 font-semibold">{progressMessage}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customization Controls */}
        <div className="space-y-6">
          {/* Colors Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Colors</h3>
            
            {/* Background Color Options */}
            <div className="space-y-3">
              <Toggle
                label="Use Background Color"
                description="Toggle to use a solid color or transparent background"
                checked={useBackgroundColor}
                onChange={setUseBackgroundColor}
              />
              
              {useBackgroundColor ? (
                <ColorPicker
                  label="Background Color"
                  color={backgroundColor}
                  onChange={setBackgroundColor}
                />
              ) : (
                <TextInput
                  label="Background Description"
                  value={backgroundDescription}
                  onChange={setBackgroundDescription}
                  placeholder="e.g., 'professional office setting with blurred background' or 'outdoor nature scene with soft bokeh'"
                  description="Describe the background you want for your mockup"
                />
              )}
            </div>

            {/* Product Color Options */}
            <div className="space-y-3">
              <Toggle
                label="Use Product Color"
                description="Apply a custom color to the product"
                checked={useProductColor}
                onChange={setUseProductColor}
              />
              
              {useProductColor && (
                <ColorPicker
                  label="Product Color"
                  color={productColor}
                  onChange={setProductColor}
                />
              )}
            </div>
          </div>

          {/* Text Overlay Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Text</h3>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Text will be professionally integrated into the mockup design, not overlaid afterwards.
              </p>
            </div>
            <TextInput
              label="Product Title"
              value={productTitle}
              onChange={setProductTitle}
              placeholder="Enter product name"
              maxLength={50}
              description="Main title that will be incorporated into the mockup design"
            />
            <TextInput
              label="Product Tagline"
              value={productSubtitle}
              onChange={setProductSubtitle}
              placeholder="Enter tagline or subtitle"
              maxLength={100}
              description="Secondary text that complements your product title"
            />
          </div>

          {/* Adjustments Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Adjustments</h3>
            <Slider
              label="Logo Scale"
              value={scale}
              onChange={setScale}
              min={50}
              max={150}
              step={5}
              unit="%"
            />
            <div className="pt-2">
              <label className="text-sm font-medium text-gray-700 block mb-2">Aspect Ratio</label>
              <div className="flex space-x-2">
                {['square', 'landscape', 'portrait'].map((ratio) => (
                  <button
                    key={ratio}
                    onClick={() => setAspectRatio(ratio)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      aspectRatio === ratio
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {ratio.charAt(0).toUpperCase() + ratio.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Effects Section */}
          <div className="bg-white rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Effects</h3>
            <Toggle
              label="Shadow Effect"
              description="Add realistic shadows for depth"
              checked={showShadow}
              onChange={setShowShadow}
            />
            <Toggle
              label="Reflection"
              description="Add a subtle reflection beneath the product"
              checked={showReflection}
              onChange={setShowReflection}
            />
            <Toggle
              label="Glossy Finish"
              description="Apply a glossy, high-shine finish to the product"
              checked={glossyFinish}
              onChange={setGlossyFinish}
            />
            <Toggle
              label="Emboss/Deboss Effect"
              description="Add raised or recessed texture to the design"
              checked={embossEffect}
              onChange={setEmbossEffect}
            />
            <Toggle
              label="Gradient Overlay"
              description="Apply a subtle gradient effect to the product"
              checked={gradientOverlay}
              onChange={setGradientOverlay}
            />
          </div>

          {/* Advanced Options */}
          <div className="bg-white rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced</h3>
            
            {/* Prompt Helper */}
            <PromptHelper 
              template={selectedTemplate || ''}
              onSuggestionClick={(suggestion) => {
                // Add suggestion to existing prompt with proper formatting
                setCustomPrompt(prev => {
                  if (prev) {
                    return `${prev}, ${suggestion}`;
                  }
                  return suggestion;
                });
              }}
            />
            
            <TextInput
              label="Custom Instructions"
              value={customPrompt}
              onChange={setCustomPrompt}
              placeholder="e.g., 'Professional fashion photography, soft studio lighting, minimalist backdrop'"
              description="Use specific descriptions for best results. Click suggestions above for ideas."
            />
          </div>

          {/* Error Banner */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
            >
              {isGenerating ? 'Generating...' : 'Generate Mockup'}
            </button>
            <button
              onClick={() => navigate('/dashboard/mockups')}
              disabled={isGenerating}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back to Templates
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Customize;
